import React from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {Alert, Col, Collapse, Row, Tooltip} from 'antd'
import dayjs from 'dayjs'
import InputNumber from '../InputNumber'
import DropdownField from '../DropdownField'
import DatePickerField from '../DatePickerField'
import SingleLineField from '../SingleLineField'
import LandingPageField from './components/LandingPageField'
import UrlSpaceFixButton from '../UrlSpaceFixButton'
import {useQuery} from '@apollo/client'
import {GET_ALL_ADVERTISERS_COUNTRY} from '../../../utils/api-utils'
import Form from '../utils/Form'
import Spinner from '../../icons/Spinner'
import {PacingSwitch} from '../../lists/listComponents/Switch'
import {InformationCircleIcon} from '@heroicons/react/24/outline'
import {
  CampaignPacing,
  CompanyType,
  CampaignType,
  CampaignTargetType,
  CampaignPriorityType,
  CampaignContentSource,
  CampaignSensitiveCategory,
  CampaignOptimisationType,
  CampaignStatus,
} from '../../../__generated__/graphql'
import {
  canRunOutsideDenmark,
  useCurrentUser,
} from '../../../utils/CurrentUserContext'
import {dailyBudgetLimitRangeRule, httpUrlRule, noSpaceRule, targetValueRangeRule, targetValueWholeNumberRule} from '../../../utils/validators'
import HiddenInput from '../HiddenInput'
import CampaignRateCardField from './components/CampaignRateCardField'
import FormItem from '../utils/FormItem'
import CampaignTypeField from './components/CampaingTypeField'
import DisableSEOField from './components/DisableSEOField'
import TrackingPixelField from './components/TrackingPixelField'
import TargetingFields from './components/TargetingFields'
import ECPMField from './components/ECPMField'
import CampaignLabelsManager from '../../widgets/CampaignLabelsManager'
import EvenPacingExtensionWarning from './components/EvenPacingExtensionWarning'
import { isNativeDisplayCampaign } from '../../../utils/api/campaign'
import { trackEvent } from '../../../utils/analytics'

const TargetTypeLabels = {
  [CampaignTargetType.Budget]: 'Budget',
  [CampaignTargetType.Clickouts]: 'Target clickouts',
  [CampaignTargetType.Cpm]: 'Target CPM',
  [CampaignTargetType.Impressions]: 'Target impressions',
  [CampaignTargetType.Reads]: 'Target reads',
}

const POLITICS_CATEGORY = CampaignSensitiveCategory.Political

const sensitiveCategoryOptions = [
  {label: 'Not Sensitive', value: CampaignSensitiveCategory.NotSensitive},
  {label: 'Political', value: CampaignSensitiveCategory.Political},
  {label: 'Gambling', value: CampaignSensitiveCategory.Gambling},
  {label: 'Adult', value: CampaignSensitiveCategory.Adult},
]

export const sanitizeSensitiveFields = (values: any = {}, includeDefault = false) => {
  const hasCategory = Object.prototype.hasOwnProperty.call(values, 'sensitiveCategory')
  const categoryValue =
    values.sensitiveCategory ?? (includeDefault ? CampaignSensitiveCategory.NotSensitive : undefined)

  if (!hasCategory && !includeDefault) {
    return values
  }

  const normalizedCategory = categoryValue || CampaignSensitiveCategory.NotSensitive

  if (normalizedCategory === POLITICS_CATEGORY) {
    return {
      ...values,
      sensitiveCategory: normalizedCategory,
    }
  }

  const {policyUrl, sponsor, ...rest} = values

  return {
    ...rest,
    sensitiveCategory: normalizedCategory,
  }
}

interface CampaignDetailsFormProps {
  className?: string
  defaultValues: {pacing: CampaignPacing.Even; [key: string]: any}
  submitButtonText: string
  onSubmit: (values: any, dirtyFields: any) => Promise<void>
  onConfirmSubmit?: (values: any, dirtyFields: any, continueCallback: () => void) => void
  isNewEntity?: boolean
  accountType: CompanyType,
  // TODO: Remove temporary priority exemption
  hidePriorityField?: boolean
  hidePacingField?: boolean
  isNativeDisplay?: boolean
  isFromDeal?: boolean
  campaignStatus?: CampaignStatus
  everActivated?: boolean
}

export const transformTargeting = (targeting: any) => {
  if (!targeting) {
    return {}
  }
  return {
    targeting: {
      ...targeting,
      location: targeting.location.reduce((acc: Location[], value: any) => {
        if (!value.region) {
          return acc
        }

        const locations = value.cities
          ? value.cities.map((city: string) => ({
              region: value.region,
              countryCode: value.countryCode,
              city,
            }))
          : [{region: value.region, countryCode: value.countryCode}]

        return [...acc, ...locations]
      }, []),
    },
  }
}


const PriorityField = ({hidden}: {hidden?: boolean}) => {
  const {user} = useCurrentUser()
  const {watch} = useFormContext()
  const campaignType = watch('type')

  if (hidden) {
    return null
  }

  const show =
    (user.company.type === CompanyType.Publisher &&
      [CampaignType.Filler, CampaignType.InHouse, CampaignType.InHouseNativeDisplay].includes(campaignType)) ||
    user.company._id === '65f59375035422efc3345d8c'

  if (!show) {
    return null
  }

  return (
    <Col span={12}>      
      <DropdownField
        top
        label='Priority'
        options={[
          {label: 'Premium', value: CampaignPriorityType.Premium},
          {
            label: 'Standard High',
            value: CampaignPriorityType.StandardHigh,
          },
          {
            label: 'Standard Medium',
            value: CampaignPriorityType.StandardMedium,
          },
          {
            label: 'Standard Low',
            value: CampaignPriorityType.StandardLow,
          },
          {
            label: 'Non Guaranteed',
            value: CampaignPriorityType.NonGuaranteed,
          },
          {
            label: 'House Ads',
            value: CampaignPriorityType.HouseAds,
          },
        ]}
        required
        htmlfor='priority'
      />
    </Col>
  )
}

const BudgetField = () => {
  const {watch, control, trigger, formState: { errors }} = useFormContext()
  const campaignType = watch('type')
  const targetType = watch('targetType') as CampaignTargetType

  // Re-validate the goal value when RHF won't on its own. Two cases: a stored value already above
  // the cap (legacy rows; CON-538 backfills them, but not necessarily before this ships), which
  // onChange-mode validation never flags because the user never edits it; and a change of delivery
  // target, which changes the applicable rules (count-based goals must be whole numbers) without
  // re-running them. Both would otherwise leave Save disabled with nothing — or the wrong thing —
  // on screen. Skipped while empty so a fresh create form doesn't open showing a required error.
  const targetValue = watch('targetValue')
  const hasTargetValue = targetValue !== undefined && targetValue !== null && targetValue !== ''
  React.useEffect(() => {
    if (!hasTargetValue) {
      return
    }

    trigger('targetValue')
  }, [hasTargetValue, targetType, trigger])

  const dailyBudgetLimit = watch('dailyBudgetLimit')
  const hasDailyBudgetLimit =
    dailyBudgetLimit !== undefined && dailyBudgetLimit !== null && dailyBudgetLimit !== ''
  React.useEffect(() => {
    if (!hasDailyBudgetLimit) {
      return
    }

    trigger('dailyBudgetLimit')
  }, [hasDailyBudgetLimit, trigger])

  if (campaignType === CampaignType.Filler) {
    return null
  }
  const campaignTarget = [CampaignType.InHouse, CampaignType.InHouseNativeDisplay].includes(campaignType) ? (
      <Col span={12}>
        <DropdownField
          top
          required
          label={'Delivery Target'}
          options={[
            {label: 'Budget', value: CampaignTargetType.Budget},
            {
              label: 'Clickouts',
              value: CampaignTargetType.Clickouts,
            },
            {
              label: 'Impressions',
              value: CampaignTargetType.Impressions,
            },
            {label: 'Reads', value: CampaignTargetType.Reads},
          ]}
          htmlfor='targetType'
        />
      </Col>
    ) : (
      <HiddenInput htmlfor='targetType' />
    )

  return (
    <>
      {campaignTarget}
      <Col span={12}>
        <FormItem
          label={
            TargetTypeLabels[targetType] ||
            TargetTypeLabels[CampaignTargetType.Budget]
          }
          top
          required={(targetType || CampaignTargetType.Budget) === CampaignTargetType.Budget && (!targetValue || !!errors?.targetValue)}
        >
          <Controller
            control={control}
            name='targetValue'
            rules={{
              validate: {
                present: (v) => (targetType || CampaignTargetType.Budget) !== CampaignTargetType.Budget || (v !== undefined && v !== null && v !== ''),
                // Unscoped by design: the API applies the cap to every target type, including
                // ones that ignore the value, because it goes live the moment targetType changes.
                range: targetValueRangeRule,
                whole: (v) => targetValueWholeNumberRule(v, targetType),
              },
            }}
            render={({field, fieldState}) => (
              <>
                <InputNumber
                  className='w-full max-w-xs'
                  size='large'
                  // Deliberately no min/max: antd clamps an out-of-range value to the bound on
                  // blur, silently rewriting what was typed (-5 -> 0, 2e9 -> 1e9). The value stays
                  // as entered and targetValueRangeRule explains what's wrong instead.
                  status={fieldState.error ? 'error' : undefined}
                  {...field}
                  suffix={
                    (targetType || CampaignTargetType.Budget) ===
                    CampaignTargetType.Budget
                      ? 'DKK'
                      : ''
                  }
                />
                {fieldState.error?.message && (
                  <div className='text-red-500 text-sm mt-1'>{fieldState.error.message}</div>
                )}
              </>
            )}
          />
        </FormItem>
      </Col>
      <Col span={12}>
        <FormItem label='Daily Budget Limit' top>
          <Controller
            control={control}
            name='dailyBudgetLimit'
            rules={{
              validate: {
                range: dailyBudgetLimitRangeRule,
              },
            }}
            render={({field, fieldState}) => {
              const rangeError =
                fieldState.error?.type === 'range' ? fieldState.error.message : undefined

              return (
                <>
                  <InputNumber
                    className='w-full max-w-xs'
                    size='large'
                    status={rangeError ? 'error' : undefined}
                    {...field}
                    suffix='DKK'
                  />
                  {rangeError && <div className='text-red-500 text-sm mt-1'>{rangeError}</div>}
                </>
              )
            }}
          />
        </FormItem>
      </Col>
    </>
  )
}

const SensitiveCategoryFields = () => {
  const {watch, setValue} = useFormContext()
  const sensitiveCategory = watch('sensitiveCategory')
  const policyUrl = watch('policyUrl')

  React.useEffect(() => {
    if (!sensitiveCategory) {
      setValue('sensitiveCategory', 'NOT_SENSITIVE', {shouldValidate: true})
    }
  }, [sensitiveCategory, setValue])

  const isPolitics = (sensitiveCategory || 'NOT_SENSITIVE') === POLITICS_CATEGORY

  return (
    <>
      <Col span={12}>
        <DropdownField
          top
          required
          label='Sensitive Category'
          htmlfor='sensitiveCategory'
          options={sensitiveCategoryOptions}
          onChange={(value: string) => {
            if (value !== POLITICS_CATEGORY && policyUrl) {
              setValue('policyUrl', '', {
                shouldDirty: true,
                shouldValidate: true,
              })
              setValue('sponsor', '', {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          }}
        />
      </Col>
      {isPolitics && (
        <Col span={12}>
          <SingleLineField
            top
            label='Policy URL'
            htmlfor='policyUrl'
            required
            type='url'
            rules={{
              validate: {
                format: httpUrlRule,
                noSpace: noSpaceRule,
              },
            }}
            footer={<UrlSpaceFixButton name="policyUrl" />}
            maxLetters="400"
          />
        </Col>
      )}
      {isPolitics && (
        <Col span={12}>
          <SingleLineField
            top
            label='Sponsor'
            htmlfor='sponsor'
            required
            rules={{
              required: 'Sponsor is required for political campaigns',
            }}
          />
        </Col>
      )}
    </>
  )
}

const PacingField = ({hidden}: {hidden?: boolean}) => {
  const {watch} = useFormContext()
  const campaignType = watch('type')

  if (hidden) {
    return null
  }

  if (campaignType === CampaignType.Filler) {
    return null
  }

  return (
    <Col span={12}>
      <div className='relative'>
        <div className='sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200'>
          <label
            htmlFor={'Pacing'}
            className='block text-sm font-medium text-gray-700 align-middle group'
          >
            <span className='align-middle flex relative'>
              Even Pacing
              <Tooltip
                title={
                  <>
                    With even pacing your budget will be spread across the
                    campaign period. <br />
                    Without even pacing the campaign
                    will be delivered as fast as possible.
                  </>
                }
              >
                <InformationCircleIcon
                  className='flex ml-1 mr-3 h-4 w-4 text-gray-400 hover:text-gray-00 hover:cursor-pointer'
                  data-tooltip-target='tooltip-default'
                />
              </Tooltip>
            </span>
          </label>
          <div className='mt-1 sm:mt-0 sm:col-span-2'>
            {<PacingSwitch name='pacing' />}
          </div>
        </div>
      </div>
    </Col>
  )
}

const OptimisationField = () => {
  const {watch} = useFormContext()
  const campaignType = watch('type')

  const options = !isNativeDisplayCampaign(campaignType) ? [
    {label: 'Reads', value: CampaignOptimisationType.Reads},
    {label: 'Clickouts', value: CampaignOptimisationType.Clickouts},
  ] : []

  return (
    <Col span={12}>
      <DropdownField
        top
        required={false}
        label={'Optimisation'}
        options={[
          {label: 'Clicks', value: CampaignOptimisationType.Ctr},
          {label: 'Conversions',value: CampaignOptimisationType.Conversions},
          ...options,
        ]}
        htmlfor='optimisation'
      />
    </Col>
  )
}

const CampaignDetailsForm: React.FC<CampaignDetailsFormProps> = (props) => {
  const {loading, error, data} = useQuery(GET_ALL_ADVERTISERS_COUNTRY, {
    variables: {},
  })

  const {user} = useCurrentUser()

  const advertisers = loading ? [] : data?.CompanyMany?.items

  if (loading) {
    return <Spinner />
  }

  // Lock start date once submitted/active or when the campaign has ever been activated
  const hasBeenActiveOrSubmitted = props.campaignStatus && [
    CampaignStatus.Ready,
    CampaignStatus.ReviewingByAdvertiser,
    CampaignStatus.Active,
    CampaignStatus.Paused,
    CampaignStatus.Finished
  ].includes(props.campaignStatus);

  // For READY campaigns, don't show past date warning (it's already approved)
  const isReady = props.campaignStatus === CampaignStatus.Ready;
  const hasEverBeenActivated = !!props.everActivated;
  const disableStartDate = hasEverBeenActivated || isReady || !!hasBeenActiveOrSubmitted;
  const suppressPastDateWarning = isReady || hasEverBeenActivated;
  const datePickerDisabled: [boolean, boolean] = [disableStartDate, false]; // [disableStart, disableEnd]
  const isExistingOrFromDeal = !props.isNewEntity || props.isFromDeal

  const countries = [
    {label: 'Denmark', value: 'Denmark'},
    {label: 'Sweden', value: 'Sweden'},
    {label: 'Germany', value: 'Germany'},
  ]

  const contentSources = [
    {label: 'Contimo', value: CampaignContentSource.ContimoContent},
    {label: 'Vev', value: CampaignContentSource.ProviderContent},
  ]

  const collapseItems = [
    {
      key: '1',
      label: 'Details',
      children: (
        <Row>
          {props.isNewEntity && canRunOutsideDenmark(user) && (
            <Col span={24}>
              <Alert
                type='info'
                showIcon
                className='mb-4'
                message={
                  <a
                    href='https://v2.contimo.app/create'
                    target='_blank'
                    rel='noreferrer'
                    className='font-semibold underline'
                    onClick={() => trackEvent('v2_app_launched', {source: 'campaign_details_alert'})}
                  >
                    Use v2 to run campaigns outside of Denmark
                  </a>
                }
              />
            </Col>
          )}
          <Col span={12}>
            <SingleLineField
              top
              label={'Campaign Name'}
              htmlfor='name'
              required
            />
          </Col>
          {[CompanyType.Agency, CompanyType.Publisher].includes(
            props.accountType
          ) ? (
            <Col span={12}>
              <DropdownField
                top={true}
                required={true}
                showSearch
                placeholder={'Select an Advertiser'}
                label={'Advertiser'}
                disabled={isExistingOrFromDeal}
                options={advertisers.map(
                  (advertiser: {name: string; _id: string}) => ({
                    label: advertiser.name,
                    value: advertiser._id,
                  })
                )}
                htmlfor='advertiserId'
              />
            </Col>
          ) : user.company ? (
            <Col span={12}>
              <DropdownField
                top={true}
                required={true}
                label={'Advertiser'}
                disabled={isExistingOrFromDeal}
                options={[
                  {
                    label: user.company.name || 'Unknown', // Handle potential undefined name
                    value: user.company._id || 'UnknownID', // Handle potential undefined _id
                  },
                ]}
                htmlfor='advertiserId'
              />
            </Col>
          ) : null}

          <CampaignTypeField disabled={isExistingOrFromDeal} />

          <Col span={12}>
            <DropdownField
              top={true}
              required={true}
              label={'Country'}
              options={countries}
              disabled
              htmlfor='country'
            />
          </Col>
          <Col span={12}>
            <LandingPageField />
          </Col>
          <TrackingPixelField />
          <Col span={12}>
            <SingleLineField
              top
              label={'Billing ID'}
              htmlfor='billingId'
              required={false}
            />
          </Col>
          <SensitiveCategoryFields />
          <Col span={12}>
            <CampaignLabelsManager />
          </Col>
          {user.company.allowVevIntegration && (
            <Col span={12}>
              <DropdownField
                top={true}
                required={true}
                label={'Content source'}
                options={contentSources}
                htmlfor='contentSource'
              />
            </Col>
          )}
        </Row>
      ),
    },
    {
      key: '2',
      label: 'Delivery and pricing',
      children: (
        <Row>
          <BudgetField />
          {/* TODO: Remove temporary priority exemption */}
          <PriorityField hidden={props.hidePriorityField} />
          <CampaignRateCardField />
          <Col span={12}>
            <DatePickerField
              top={true}
              label='Campaign Period'
              name='campaignPeriod'
              minDate={dayjs().add(10, 'minute')}
              disabled={datePickerDisabled}
              suppressPastDateWarning={suppressPastDateWarning}
              required
            />
          </Col>
          <EvenPacingExtensionWarning
            isNewEntity={props.isNewEntity}
            hidePacingField={props.hidePacingField}
            everActivated={props.everActivated}
          />
          <ECPMField />
          <PacingField hidden={props.hidePacingField} />
          <DisableSEOField />
          <OptimisationField />
        </Row>
      ),
    },
    {key: '3', label: 'Targeting', children: <TargetingFields />},
  ]
  return (
    <Form {...props}>
      <div className='gap-x-20 gap-y-10 divide-y divide-dashed mt-5'>
        <Collapse items={collapseItems} defaultActiveKey={['1', '2']} />
      </div>
    </Form>
  )
}

export default CampaignDetailsForm
